import { getApiBaseUrl } from '../config';
import { jitsiDefaultSettings } from '../types/jitsiDefaults';
import type { JitsiMeetingSettingKeys } from '../types/jitsiSettings';
import { DEVICE_LS_KEYS } from './deviceUtils';
import { isDevelopment } from './environment';
import {
  FULLSCREEN_BUTTON_ID,
  FULLSCREEN_TOOLBAR_BUTTON,
  isMeetingFullscreenSupported,
} from './meetingFullscreen';
import { isDev } from './optimizedImports';

interface BuildJitsiConfigOverwriteParams {
  isAuthenticated: boolean;
  meetingId: string;
  meetingDisplayName: string | null;
  extraParams?: Record<string, string>;
}

export const toBoolean = (val: unknown): boolean => {
  if (typeof val === 'boolean') {
    return val;
  }
  if (typeof val === 'string') {
    return val.toLowerCase() === 'true';
  }
  return Boolean(val);
};

// biome-ignore lint/suspicious/noExplicitAny: The configOverwrite can have various types of values, so we use any here for flexibility.
export function buildConfigOverwrite(settings: Partial<Record<JitsiMeetingSettingKeys, any>>) {
  // biome-ignore lint/suspicious/noExplicitAny: The settings object can have various types of values, so we use any here for flexibility.
  const result: any = {};

  Object.entries(settings).forEach(([path, value]) => {
    if (value === undefined) {
      return;
    }

    const keys = path.split('.');
    let current = result;

    keys.forEach((key, index) => {
      if (index === keys.length - 1) {
        current[key] = value;
      } else {
        current[key] ??= {};
        current = current[key];
      }
    });
  });

  return result;
}

/**
 * Builds the Jitsi configOverwrite object from stored settings, device preferences, and extra params
 */
export const buildJitsiConfigOverwrite = ({
  isAuthenticated,
  meetingId,
  meetingDisplayName,
  extraParams,
}: BuildJitsiConfigOverwriteParams) => {
  // biome-ignore lint/suspicious/noExplicitAny: The configOverwrite can have various types of values, so we use any here for flexibility.
  let configOverwrite: any = {};
  try {
    const storedSettings = localStorage.getItem('sangoma-meet-settings');
    const meetings = storedSettings ? JSON.parse(storedSettings)?.meetings : {};
    const devices = storedSettings ? JSON.parse(storedSettings)?.devices : {};

    const allowedSettings = {} as Record<JitsiMeetingSettingKeys, unknown>;

    (['prejoinConfig.enabled', 'disableShortcuts'] as const).forEach((key) => {
      const defaultVal = jitsiDefaultSettings[key] ?? false;
      if (meetings && key in meetings) {
        allowedSettings[key] = toBoolean(meetings?.[key] ?? defaultVal);
      } else {
        allowedSettings[key] = defaultVal;
      }
    });

    (['startWithAudioMuted', 'startWithVideoMuted'] as const).forEach((key) => {
      const defaultVal = jitsiDefaultSettings[key] ?? false;
      if (devices && key in devices) {
        allowedSettings[key] = toBoolean(devices?.[key] ?? defaultVal);
      } else {
        allowedSettings[key] = defaultVal;
      }
    });

    Object.entries(extraParams || {}).forEach(([key, value]) => {
      if (key === 'prejoinConfig.enabled') {
        allowedSettings[key as JitsiMeetingSettingKeys] = toBoolean(value);
      } else if (key === 'startWithAudioMuted') {
        allowedSettings[key as JitsiMeetingSettingKeys] = toBoolean(value);
        allowedSettings.startAudioMuted = toBoolean(value);
      } else if (key === 'startWithVideoMuted') {
        allowedSettings[key as JitsiMeetingSettingKeys] = toBoolean(value);
        allowedSettings.startVideoMuted = toBoolean(value);
      } else {
        const lower = (value || '').toLowerCase();
        const isBooleanString = lower === 'true' || lower === 'false';
        const finalValue = isBooleanString ? toBoolean(value) : value;
        allowedSettings[key as JitsiMeetingSettingKeys] = finalValue;
      }
    });

    const preselectedDevices = {
      video: localStorage.getItem(DEVICE_LS_KEYS.camera) || undefined,
      audioInput: localStorage.getItem(DEVICE_LS_KEYS.microphone) || undefined,
      audioOutput: localStorage.getItem(DEVICE_LS_KEYS.audioOutput) || undefined,
    };

    Object.entries(preselectedDevices).forEach(([deviceKey, value]) => {
      if (value !== undefined) {
        const flatKey = `prejoinConfig.preselectedDevices.${deviceKey}`;
        allowedSettings[flatKey as JitsiMeetingSettingKeys] = value;
      }
    });

    const isDevMode = isDevelopment();
    const apiBase = getApiBaseUrl(isDevMode);
    const dialInBaseUrl = isDevMode ? `${window.location.origin}${apiBase}` : apiBase;

    const staticConfig = {
      useHostPageLocalStorage: true,
      readOnlyName: true,
      hideEmailInSettings: !isAuthenticated,
      brandingRoomAlias: meetingId,
      callDisplayName: meetingDisplayName || meetingId,
      dropbox: { appKey: null },
      fileSharing: { enabled: false },
      inviteAppName: 'Sangoma Meet',
      dialInConfCodeUrl: `${dialInBaseUrl}/phones/app/conf-mapper`,
      dialInNumbersUrl: `${dialInBaseUrl}/phones/app/phone-list`,
      dialInEnabled: true,
      apiLogLevels: ['error', 'warn', 'log', 'info', 'debug'],
      deeplinking: {
        desktop: {
          enabled: false,
        },
        disabled: true,
      },
      p2p: {
        enabled: false,
      },
      toolbarButtons: [
        'microphone',
        'camera',
        'chat',
        'closedcaptions',
        'desktop',
        // 'download',
        // 'embedmeeting',
        'etherpad',
        'feedback',
        'filmstrip',
        // Jitsi's native 'fullscreen' button (and its "S" shortcut) fullscreens the 8x8.vc
        // iframe, so the browser labels the exit prompt "8x8.vc". Ours fullscreens the host page.
        FULLSCREEN_BUTTON_ID,
        'hangup',
        'help',
        'highlight',
        'invite',
        // 'linktosalesforce',
        // 'livestreaming',
        // 'noisesuppression',
        'participants-pane',
        'profile',
        'raisehand',
        'recording',
        // 'security',
        'select-background',
        'settings',
        // 'shareaudio',
        // 'sharedvideo',
        // 'shortcuts',
        'stats',
        'tileview',
        'toggle-camera',
        // 'videoquality',
        'whiteboard',
      ],
      customToolbarButtons: isMeetingFullscreenSupported() ? [FULLSCREEN_TOOLBAR_BUTTON] : [],
    };

    Object.entries(staticConfig).forEach(([key, value]) => {
      allowedSettings[key as JitsiMeetingSettingKeys] = value;
    });

    configOverwrite = buildConfigOverwrite(allowedSettings);
  } catch (e) {
    if (isDev) {
      console.error('[App] Error reading meeting settings:', e);
    }
  }

  return configOverwrite;
};
